/**
 * ConfigurationFlowEditor Component
 * Main component for viewing and editing the configuration question flow
 */

import React, { useState, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { Button, Tag, Spin, Empty, Modal, Form, Input, Tabs } from 'antd';
import { toast } from 'react-hot-toast';
import {
  ExportOutlined,
  QuestionCircleOutlined,
  CloudOutlined,
  CloudSyncOutlined,
} from '@ant-design/icons';
import { VersionHistoryPanel } from './VersionHistoryPanel';
import { IChatBotLiveConfig } from '../../services/chatbotConfigGenerator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useProductList } from '../../hooks/productSetup';
import { useCategoryList } from '../../hooks/categorySetup';
import { useAllConfigOptions } from '../../hooks/configOption';
import { useChatbotFlow } from '../../hooks/useChatbotFlow';
import { usePostConfigurationJson, useGetProductConfigurationJson, useGetConfigMutation, usePutConfigLive, useGetConfigVersions } from '../../hooks/productConfiguration';
import { IFlowQuestion } from '../../utils/jsonFlowGenerator';
import { QuestionCard } from './QuestionCard';
import { QuestionEditModal } from './QuestionEditModal';
import { ConfigurationViewTab } from '../configuration-setup/ConfigurationViewTab';





export const ChatbotFlowEditor: React.FC = () => {
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username ?? '';

  const [editingQuestion, setEditingQuestion] = useState<IFlowQuestion | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveForm] = Form.useForm();
  const [previewVersionConfig, setPreviewVersionConfig] = useState<IChatBotLiveConfig | null>(null);
  const [previewVersionLabel, setPreviewVersionLabel] = useState<string | null>(null);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Fetch data using existing hooks
  const { data: productsData, isLoading: productsLoading } = useProductList();
  const { data: categoriesData, isLoading: categoriesLoading } = useCategoryList();
  const { data: optionsData, isLoading: optionsLoading } = useAllConfigOptions();

  const products = productsData?.value || [];
  const categories = categoriesData?.value || [];
  const options = optionsData || [];

  // Backend configuration
  const { data: backendConfigData, isLoading: backendConfigLoading } = useGetProductConfigurationJson();

  // Save mutation
  const { mutate: saveConfiguration, isPending: isSaving } = usePostConfigurationJson();
  const { mutateAsync: putConfigLive, isPending: isPuttingLive } = usePutConfigLive();
  const configMuatation = useGetConfigMutation();
  const { data: versionsData } = useGetConfigVersions();
  // Parse backend config and extract remote question flow
  const { backendConfig, remoteQuestionFlow } = useMemo(() => {
    if (!backendConfigData) {
      return { backendConfig: undefined, remoteQuestionFlow: undefined };
    }

    // Check if Config is a string that needs parsing
    let configData = backendConfigData.Config;
    if (typeof configData === 'string') {
      try {
        configData = JSON.parse(configData);
      } catch (e) {
        return { backendConfig: backendConfigData, remoteQuestionFlow: undefined };
      }
    }

    const questionFlow = configData?.questionFlow as unknown as IFlowQuestion[] | undefined;

    return {
      backendConfig: { ...backendConfigData, Config: configData },
      remoteQuestionFlow: questionFlow
    };
  }, [backendConfigData]);

  const {
    questions,
    customizations,
    questionOrder: _questionOrder,
    optionOrders: _optionOrders,
    hasUnsavedChanges,
    isLoading: flowLoading,
    updateCustomization,
    updateQuestionOrder,
    updateOptionOrder,
    getQuestionWithCustomization,
    resetCustomizations,
  } = useChatbotFlow({
    products,
    categories,
    options,
    remoteFlow: remoteQuestionFlow,
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const isLoading = productsLoading || categoriesLoading || optionsLoading || flowLoading || backendConfigLoading;

  // Get unique categories from questions
  const uniqueCategories = useMemo(() => {
    const categorySet = new Set(questions.map(q => q.categoryName));
    return Array.from(categorySet);
  }, [questions]);

  // Get customized questions for the Configuration View tab
  const customizedQuestionsForView = useMemo(() => {
    return questions.map(question => getQuestionWithCustomization(question));
  }, [questions, getQuestionWithCustomization]);

  // Handle drag start — track which item is being dragged
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  // Handle drag end — single drag OR group drag if the dragged item is selected
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const currentOrder = questions.map(q => q.questionId);
    const draggedId = active.id as string;
    const overId = over.id as string;
    const selectedSet = new Set(selectedQuestionIds);
    const isGroupDrag = selectedSet.has(draggedId) && selectedQuestionIds.length > 1;

    if (isGroupDrag) {
      // Move all selected items as a block to the drop position
      const activeIndex = currentOrder.indexOf(draggedId);
      const overIndex = currentOrder.indexOf(overId);

      const selected = currentOrder.filter(id => selectedSet.has(id));
      const remaining = currentOrder.filter(id => !selectedSet.has(id));
      const overInRemaining = remaining.indexOf(overId);

      let insertAt: number;
      if (overInRemaining >= 0) {
        // Insert after the target when dragging down, before when dragging up
        insertAt = overIndex > activeIndex ? overInRemaining + 1 : overInRemaining;
      } else {
        insertAt = remaining.length;
      }

      const newOrder = [
        ...remaining.slice(0, insertAt),
        ...selected,
        ...remaining.slice(insertAt),
      ];
      updateQuestionOrder(newOrder);
      toast.success(`Moved ${selected.length} questions`);
    } else {
      // Normal single-item drag
      const oldIndex = currentOrder.indexOf(draggedId);
      const newIndex = currentOrder.indexOf(overId);
      if (oldIndex !== -1 && newIndex !== -1) {
        updateQuestionOrder(arrayMove(currentOrder, oldIndex, newIndex));
        setSelectedQuestionIds([]);
        toast.success('Question order updated');
      }
    }
  };

  // Handle checkbox toggle
  const handleToggleSelect = (questionId: string, checked: boolean) => {
    setSelectedQuestionIds(prev =>
      checked ? [...prev, questionId] : prev.filter(id => id !== questionId)
    );
  };

  // Handle question edit
  const handleEditQuestion = (question: IFlowQuestion) => {
    setEditingQuestion(question);
  };

  // Handle save customization
  const handleSaveCustomization = (questionId: string, customization: any) => {
    updateCustomization(questionId, customization);
    setEditingQuestion(null);
    toast.success('Question updated successfully');
  };

  // Handle export to JSON
  const handleExportJson = () => {
    const chatbotConfig = {
      version: backendConfig?.Version || '1.0.0',
      configName: backendConfig?.Config?.configName || 'EcoSlide Configuration',
      lastUpdated: new Date().toISOString(),
      brandName: 'Victorian Sliders',
      productLine: 'EcoSlide Vertical Sliding Sash Windows',
      chatbotConfig: {
        greeting: "Hello! I'm here to help you configure your perfect window.",
        language: 'en-GB',
        currency: 'GBP',
        units: 'metric',
        defaultRegion: 'UK',
      },
      questionFlow: questions.map(q => getQuestionWithCustomization(q)),
      metadata: {
        generatedAt: new Date().toISOString(),
        totalCategories: uniqueCategories.length,
        totalQuestions: questions.length,
      },
    };

    const jsonStr = JSON.stringify(chatbotConfig, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ecoslide-config-${chatbotConfig.version}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Configuration exported successfully');
  };

  // Handle save configuration
  const handleSaveConfiguration = async () => {
    try {
      const values = await saveForm.validateFields();
      // Fetch latest config data to get current validation and pricing rules
      const latestConfigData = await configMuatation.mutateAsync();
      // Get existing rules from latest config to preserve them
      const existingValidationRules = latestConfigData?.Config?.validationRules || [];
      const existingPricingRules = latestConfigData?.Config?.pricingRules || [];

      const chatbotConfig = {
        version: values.version,
        configName: values.configName,
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: currentUserEmail,
        brandName: 'Victorian Sliders',
        productLine: 'EcoSlide Vertical Sliding Sash Windows',
        chatbotConfig: {
          greeting: values.greeting || "Hello! I'm here to help you configure your perfect window.",
          language: 'en-GB',
          currency: 'GBP',
          units: 'metric',
          defaultRegion: 'UK',
        },
        questionFlow: questions.map(q => getQuestionWithCustomization(q)),
        validationRules: existingValidationRules,
        pricingRules: existingPricingRules,
        metadata: {
          generatedAt: new Date().toISOString(),
          totalCategories: uniqueCategories.length,
          totalQuestions: questions.length,
        },
      };

      // Payload structure matching the API
      const payload = {
        Version: values.version,
        Config: chatbotConfig,
      } as unknown as IChatBotLiveConfig;

      saveConfiguration(payload, {
        onSuccess: async () => {
          try {
            await putConfigLive(payload);
            toast.success('Configuration synced to live successfully');
          } catch {
            toast.error('Config uploaded but failed to sync to live');
          }
          resetCustomizations();
          setShowSaveModal(false);
          saveForm.resetFields();
        },
      });
    } catch (error) {
    }
  };

  // Handle version preview
  const handlePreviewVersion = (config: IChatBotLiveConfig, versionLabel: string) => {
    setPreviewVersionConfig(config);
    setPreviewVersionLabel(versionLabel);
  };

  const handleClearPreview = () => {
    setPreviewVersionConfig(null);
    setPreviewVersionLabel(null);
  };

  // Version validation rule
  const versionValidator = (_: unknown, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Version is required'));
    }
    const versionPattern = /^\d+\.\d+\.\d+$/;
    if (!versionPattern.test(value)) {
      return Promise.reject(new Error('Version must be in format: number.number.number (e.g., 1.0.0)'));
    }
    return Promise.resolve();
  };

  // Highest version from the versions list (avoids collisions after a revert)
  const getHighestVersion = (): string | null => {
    const versions = versionsData?.versions || [];
    const parsed = versions
      .map(v => v.replace(/^v/, '').replace(/_\d+$/, '')) // "v3.1.78_20260303" → "3.1.78"
      .map(v => v.split('.').map(Number))
      .filter(parts => parts.length === 3 && parts.every(n => !isNaN(n)));
    if (parsed.length === 0) return null;
    const highest = parsed.sort((a, b) => {
      for (let i = 0; i < 3; i++) {
        const diff = (b[i] ?? 0) - (a[i] ?? 0);
        if (diff !== 0) return diff;
      }
      return 0;
    })[0];
    if (!highest) return null;
    return highest.join('.');
  };

  // Suggest patch-incremented version from the highest known version
  const getSuggestedVersion = () => {
    const highest = getHighestVersion();
    const base = highest ?? backendConfig?.Version ?? null;
    if (!base) return '1.0.0';
    const parts = base.split('.').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return '1.0.0';
    parts[2] = (parts[2] ?? 0) + 1;
    return parts.join('.');
  };

  // Derive raw IFlowQuestion[] and transformed Question[] from the versioned config
  const { previewFlowQuestions, previewQuestionsForView } = useMemo(() => {
    if (!previewVersionConfig) return { previewFlowQuestions: null, previewQuestionsForView: null };
    let configData = previewVersionConfig.Config;
    if (typeof configData === 'string') {
      try { configData = JSON.parse(configData as unknown as string); } catch {
        return { previewFlowQuestions: null, previewQuestionsForView: null };
      }
    }
    const questionFlow = (configData as any)?.questionFlow as IFlowQuestion[] | undefined;
    if (!questionFlow) return { previewFlowQuestions: null, previewQuestionsForView: null };
    return {
      previewFlowQuestions: questionFlow,
      previewQuestionsForView: questionFlow,
    };
  }, [previewVersionConfig]);

  // Active data — versioned config overrides live when preview is active
  const activeBackendConfig = previewVersionConfig ?? backendConfig;
  const activeFlowQuestions = previewFlowQuestions ?? questions;
  const activeQuestionsForView = previewQuestionsForView ?? customizedQuestionsForView;
  const activeUniqueCategories = useMemo(() => {
    const categorySet = new Set(activeFlowQuestions.map(q => q.categoryName));
    return Array.from(categorySet);
  }, [activeFlowQuestions]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" tip="Loading flow data..." />
      </div>
    );
  }

  // Render empty state
  if (questions.length === 0) {
    return (
      <div className="bg-white rounded-lg p-6">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              No questions generated yet.
              <br />
              Please add products, categories, and options first.
            </span>
          }
        >
          <Button type="primary" icon={<QuestionCircleOutlined />}>
            Go to Configuration Options
          </Button>
        </Empty>
      </div>
    );
  }

  // Configuration Editor Tab Content
  const editorTab = (
    <div>
      {/* Version History Panel */}
      <VersionHistoryPanel
        onPreviewVersion={handlePreviewVersion}
        onClearPreview={handleClearPreview}
        previewVersionLabel={previewVersionLabel}
        previewVersionConfig={previewVersionConfig}
        activeVersion={backendConfig?.Version}
        activeConfigName={backendConfig?.Config?.configName}
      />

      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">
            Configuration Flow Editor
          </h2>
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>{activeFlowQuestions.length} Questions</span>
            <span>|</span>
            <span>{activeUniqueCategories.length} Categories</span>
            {!previewVersionLabel && (
              <>
                <span>|</span>
                <span>{Object.keys(customizations).filter(k => customizations[k]?.questionText || customizations[k]?.helperText).length} Customized</span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Live Sync Status */}
          {previewVersionLabel ? (
            <Tag color="orange">
              Preview: v{previewVersionLabel.replace(/^v/, '').replace(/_\d+$/, '')}
            </Tag>
          ) : hasUnsavedChanges ? (
            <>
              <Tag icon={<CloudSyncOutlined />} color="orange">
                Unsaved Changes
              </Tag>
              <Button size="small" danger onClick={resetCustomizations}>
                Discard Changes
              </Button>
            </>
          ) : backendConfig ? (
            <Tag icon={<CloudOutlined />} color="green">
              Live: v{backendConfig.Version}
            </Tag>
          ) : (
            <Tag icon={<CloudOutlined />} color="default">
              Live: No Config
            </Tag>
          )}

          {/* Clear selection */}
          {selectedQuestionIds.length > 0 && (
            <Button
              size="small"
              onClick={() => setSelectedQuestionIds([])}
            >
              Clear Selection ({selectedQuestionIds.length})
            </Button>
          )}

          {/* Actions — hide sync/export while previewing an old version */}
          {!previewVersionLabel && (
            <>
              <Button
                type="primary"
                icon={<CloudOutlined />}
                onClick={() => setShowSaveModal(true)}
              >
                Sync To Live
              </Button>
              <Button
                icon={<ExportOutlined />}
                onClick={handleExportJson}
              >
                Export to JSON
              </Button>
            </>
          )}
        </div>
      </div>


      {/* Questions List */}
      {previewVersionLabel ? (
        /* Read-only list when previewing a historical version */
        <div className="space-y-3">
          {activeFlowQuestions.map((question) => (
            <QuestionCard
              key={question.questionId}
              question={question}
              onEdit={() => {}}
              onUpdateOptionOrder={() => {}}
              isCustomized={false}
            />
          ))}
        </div>
      ) : (
        /* Live drag-and-drop editing */
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={questions.map(q => q.questionId)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {questions.map((question) => {
                const customization = customizations[question.questionId];
                const isCustomized = !!(customization?.questionText || customization?.helperText || question.isCustomModified);
                const questionWithCustomization = getQuestionWithCustomization(question);

                return (
                  <QuestionCard
                    key={question.questionId}
                    question={questionWithCustomization}
                    onEdit={handleEditQuestion}
                    onUpdateOptionOrder={updateOptionOrder}
                    isCustomized={isCustomized}
                    isSelected={selectedQuestionIds.includes(question.questionId)}
                    onToggleSelect={handleToggleSelect}
                  />
                );
              })}
            </div>
          </SortableContext>

          <DragOverlay dropAnimation={null}>
            {activeId && selectedQuestionIds.includes(activeId) && selectedQuestionIds.length > 1 ? (
              <div className="bg-white border-2 border-blue-400 rounded-lg shadow-2xl w-96 overflow-hidden opacity-95">
                <div className="bg-blue-500 px-3 py-1.5 flex items-center gap-2">
                  <span className="text-white text-xs font-semibold">
                    Moving {selectedQuestionIds.length} questions
                  </span>
                </div>
                <div className="divide-y divide-gray-100 max-h-60 overflow-hidden">
                  {questions
                    .filter(q => selectedQuestionIds.includes(q.questionId))
                    .map((q, i) => (
                      <div key={q.questionId} className="px-3 py-1.5 flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-4 shrink-0">{i + 1}</span>
                        <span className="text-xs text-gray-700 truncate">{q.question}</span>
                      </div>
                    ))}
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );

  // Configuration View Tab Content - only show if we have questions
  const viewTab = isLoading || questions.length === 0 ? (
    <div className="flex items-center justify-center py-12">
      <Spin size="large" tip="Loading configuration..." />
    </div>
  ) : (
    <ConfigurationViewTab
      questions={activeQuestionsForView}
      backendConfig={activeBackendConfig}
      loading={false}
    />
  );

  return (
    <div className="bg-white rounded-lg p-4">
      <Tabs
        defaultActiveKey="editor"
        items={[
          {
            key: 'editor',
            label: 'Flow Editor',
            children: editorTab,
          },
          {
            key: 'view',
            label: previewVersionLabel
              ? `Configuration View (Preview: ${previewVersionLabel.replace(/^v/, '').replace(/_\d+$/, '')})`
              : 'Configuration View',
            children: viewTab,
          },
        ]}
      />

      {/* Edit Modal */}
      <QuestionEditModal
        open={!!editingQuestion}
        question={editingQuestion}
        customization={editingQuestion ? customizations[editingQuestion.questionId] : undefined}
        onSave={handleSaveCustomization}
        onCancel={() => setEditingQuestion(null)}
      />

      {/* Save Configuration Modal */}
      <Modal
        title="Sync Configuration to Live"
        open={showSaveModal}
        onOk={handleSaveConfiguration}
        onCancel={() => {
          setShowSaveModal(false);
          saveForm.resetFields();
        }}
        okText="Save Configuration"
        okButtonProps={{ loading: isSaving || configMuatation.isPending || isPuttingLive }}
        width={500}
        afterOpenChange={(open) => {
          if (open) {
            saveForm.setFieldsValue({
              version: getSuggestedVersion(),
              configName: backendConfig?.Config?.configName || 'EcoSlide Configuration',
              greeting: backendConfig?.Config?.chatbotConfig?.greeting || "Hello! I'm here to help you configure your perfect window.",
            });
          }
        }}
      >
        <Form
          form={saveForm}
          layout="vertical"
          className="mt-4"
        >
          <Form.Item
            label="Version"
            name="version"
            rules={[{ validator: versionValidator }]}
            extra={
              <span>
                Format: major.minor.patch (e.g., 1.0.0, 2.1.3)
                {getHighestVersion() && (
                  <span className="ml-2 text-blue-500">
                    | Latest in history: v{getHighestVersion()}
                  </span>
                )}
                {backendConfig?.Version && (
                  <span className="ml-2 text-gray-400">
                    | Live: v{backendConfig.Version}
                  </span>
                )}
              </span>
            }
          >
            <Input placeholder="1.0.0" />
          </Form.Item>

          <Form.Item
            label="Configuration Name"
            name="configName"
            rules={[{ required: true, message: 'Configuration name is required' }]}
          >
            <Input placeholder="Enter configuration name" />
          </Form.Item>

          <Form.Item
            label="Assistant Greeting Message"
            name="greeting"
            rules={[{ required: true, message: 'Greeting message is required' }]}
          >
            <Input.TextArea
              placeholder="Enter the greeting message shown to users"
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>

          <div className="bg-gray-50 rounded p-3 text-sm text-gray-600">
            <div className="font-medium mb-2">Configuration Summary:</div>
            <div className="space-y-1">
              <div>Total Questions: {questions.length}</div>
              <div>Total Categories: {uniqueCategories.length}</div>
              <div>Customized Questions: {Object.keys(customizations).filter(k => customizations[k]?.questionText || customizations[k]?.helperText).length}</div>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
